import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { authenticationApi } from "apis";
import { EFirebaseErrorMessage } from "constants/firebaseError";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useForm } from "react-hook-form";
import { updatePasswordSchema } from "validations";

export type UpdatePasswordFormType = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

export const useUpdatePassword = () => {
  const { updateOwnPassword } = useAuth();
  const { showError, showSuccess } = useToast();

  const methods = useForm<UpdatePasswordFormType>({
    resolver: yupResolver(updatePasswordSchema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: UpdatePasswordFormType) => {
      await updateOwnPassword(data.currentPassword, data.newPassword);
      await authenticationApi.authSendPasswordUpdatedEmail();
    },
    onSuccess: () => {
      showSuccess("Update successful", "Your password has been updated.");
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Update failed. Please try again.";

      // Inline field error for incorrect current password
      if (
        error instanceof Error &&
        error.message === EFirebaseErrorMessage.WRONG_PASSWORD
      ) {
        methods.setError("currentPassword", {
          type: "manual",
          message: "Incorrect password",
        });
        return; // Avoid duplicate toast for this common case
      }

      showError("Update failed", errorMessage);
    },
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    // Business rule: new password must not equal current password
    if (data.currentPassword === data.newPassword) {
      methods.setError("currentPassword", {
        type: "manual",
        message: "Must not match the current password",
      });
      return;
    }
    mutation.mutate(data);
  });

  return {
    methods,
    onSubmit,
    isSubmitting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    resetMutation: mutation.reset,
  };
};
